import React, { use, useMemo } from "react";
import { DeleteOutlined, EditOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";
import { Badge, Button, Popconfirm, Space, Tooltip } from "antd";

import { Base } from "@binale-tech/shared";
import { CompanyAccountingYearContext, CompanyContext } from "scripts/context/CompanyContext";

import { Category, GenericRecord } from "../../../../scripts/models";
import { gray, yellow } from "@ant-design/colors";
import "./styles.css";
import { clsx } from "clsx";
import { ColumnDefinition, VirtualTable } from "@app/components/shared/Table2/VirtualTable";
import { PlaceholderActions } from "@app/components/shared/Table2/PlaceholderActions";
import { useOnScreen } from "scripts/infrastructure/hooks/useOnScreen";
import dayjs from "dayjs";

interface RowActionsProps {
    record: TableItemType;
    onMarkClick?: (v: Base.INumName, mark: boolean) => void;
    onClick?: (v: Base.INumName) => void;
    onDeleteClick?: (v: Base.INumName) => void;
    isRemovable?: (v: Base.INumName) => boolean;
}
const RowActionsContent: React.FC<RowActionsProps> = React.memo(
    ({ record, onMarkClick, onClick, onDeleteClick, isRemovable }) => {
        if (record.extra.disabled) {
            return null;
        }

        return (
            <Space>
                {onMarkClick &&
                    ((record.item as Category).marked ? (
                        <Button
                            size="small"
                            type="text"
                            shape="circle"
                            icon={<StarFilled style={{ color: yellow[5] }} />}
                            onClick={() => onMarkClick(record.item, false)}
                        />
                    ) : (
                        <Button
                            size="small"
                            type="text"
                            shape="circle"
                            icon={<StarOutlined style={{ color: yellow[5] }} />}
                            onClick={() => onMarkClick(record.item, true)}
                        />
                    ))}
                <Button
                    size="small"
                    type="text"
                    shape="circle"
                    icon={<EditOutlined />}
                    onClick={() => onClick(record.item)}
                />
                <Popconfirm
                    title={<FormattedMessage id="app.confirmation.header" />}
                    onConfirm={e => {
                        e.stopPropagation();
                        onDeleteClick(record.item);
                    }}
                    okText={<FormattedMessage id="app.button.confirm" />}
                    cancelText={<FormattedMessage id="app.button.cancel" />}
                >
                    <Tooltip title={record.extra.isUsed ? "verwendet" : undefined}>
                        <Badge dot={record.extra.isUsed} color="blue" styles={{ indicator: { top: 3 } }}>
                            <Button
                                size="small"
                                type="text"
                                shape="circle"
                                icon={<DeleteOutlined />}
                                disabled={isRemovable ? !isRemovable(record.item) : false}
                            />
                        </Badge>
                    </Tooltip>
                </Popconfirm>
            </Space>
        );
    },
    (prevProps, nextProps) => {
        return prevProps.record._key === nextProps.record._key;
    }
);

const RowActions: React.FC<RowActionsProps> = props => {
    const ref = React.useRef<HTMLDivElement>(null);
    const isVisible = useOnScreen(ref);

    return <div ref={ref}>{isVisible ? <RowActionsContent {...props} /> : <PlaceholderActions />}</div>;
};

interface Props {
    items: { entity: Base.INumName; disabled?: boolean; isUsed?: boolean; defaultCategory?: Base.INumName }[];
    onClick?: (v: Base.INumName) => void;
    onDeleteClick?: (v: Base.INumName) => void;
    isRemovable?: (v: Base.INumName) => boolean;
    showDefaultName?: boolean;
    onMarkClick?: (v: Base.INumName, marked: boolean) => void;
}

type TableItemType = {
    item: Base.INumName;
    extra: { isUsed?: boolean; disabled?: boolean; defaultCategory?: Base.INumName };
    key: number;
    _key: string;
};

export const NumNameTable: React.FC<Props> = ({
    items,
    isRemovable,
    onClick,
    onDeleteClick,
    showDefaultName,
    onMarkClick,
}) => {
    const { yearConfig } = use(CompanyAccountingYearContext);

    const getNum = (i: any, kontoExt: number) => {
        if (typeof i.getExtNumPrint === "function") {
            return i.getExtNumPrint(kontoExt);
        }
        return i.num;
    };

    const getterKonto = (tableItem: TableItemType) => getNum(tableItem.item, yearConfig?.kontoExt ?? 0);
    const getterBezeichnung = (tableItem: TableItemType) => {
        const override = tableItem.extra.defaultCategory?.name;
        const { name } = tableItem.item;
        if (showDefaultName && override === tableItem.item.name) {
            return <span style={{ color: gray[1] }}>{override}</span>;
        }
        return name;
    };
    const getterDefaultName = (tableItem: TableItemType) => tableItem.extra.defaultCategory?.name;

    const itemsCache: TableItemType[] = useMemo(() => {
        return items.map((i, idx) => {
            return {
                key: idx + 1,
                item: i.entity,
                _key: i.entity.key,
                extra: { disabled: i.disabled, defaultCategory: i.defaultCategory, isUsed: i.isUsed },
            };
        });
    }, [items]);

    const columns: ColumnDefinition<TableItemType>[] = [
        {
            key: "nr",
            width: 60,
            headers: [<FormattedMessage key={"nr"} id="app.fields.lfdnr" />],
            render: (tableItem: TableItemType) => String(tableItem.key),
            className: "ManageAccount__Column--numeric",
        },
        {
            key: "num",
            width: 100,
            headers: [<FormattedMessage key={"num"} id="app.fields.konto" />],
            render: getterKonto,
            className: "ManageAccount__Column--numeric",
        },
        showDefaultName
            ? {
                  key: "defaultName",
                  width: 540,
                  headers: [
                      <span key="defaultName">
                          Original <FormattedMessage id="app.fields.bezeichnung" />
                      </span>,
                  ],
                  render: getterDefaultName,
                  ellipsis: true,
              }
            : null,
        {
            key: "name",
            width: 540,
            header: <FormattedMessage key={"name"} id="app.fields.bezeichnung" />,
            render: getterBezeichnung,
            ellipsis: true,
        },
        {
            key: "actions",
            width: 110,
            headers: [],
            render: (record: TableItemType) => {
                if (record.extra.disabled) {
                    return undefined;
                }
                return (
                    <RowActions
                        record={record}
                        onMarkClick={onMarkClick}
                        onClick={onClick}
                        onDeleteClick={onDeleteClick}
                        isRemovable={isRemovable}
                    />
                );
            },
        },
    ].filter(Boolean);

    return (
        <div style={{ display: "flex", position: "relative", height: "100%", width: "100%", maxWidth: 1400 }}>
            <VirtualTable<TableItemType>
                itemHeight={25}
                data={itemsCache}
                rowDefinition={{
                    getRowClassNames: item => {
                        return clsx("ManageAccount__Row", {
                            "ManageAccount__Row--disabled": item.extra.disabled,
                        });
                    },
                }}
                columnDefinitions={columns}
                listProps={{ className: "ManageAccount__Table" }}
            />
        </div>
    );
};
